import { type FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "widgets";
import { useURLSearchParam, useURLSearchParamSingleValue } from "widgets/hooks";

import { type Group } from "@/graphql.ts";
import { createSearchResultFromURL } from "@/redux/modules/search-results.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, VALIDATION_ERROR } from "@/settings/settings.js";

import InformationPage from "./errors/InformationPage.js";

interface RejectReason {
  error: { message: string; graphQLErrors: { type: string }[] | null };
  payload: any;
  type: any;
}

/**
 * Component for looking up the search query parameters from URL and redirecting to proper search results page.
 * Usually useful for linking to Selector from external custom systems.
 * e.g. http://localhost:8080/searchQuery?searchType=gene&query=name=='tcap';genome.name=='mmus33'
 * e.g. http://localhost:8080/searchQuery?searchType=gene&query=(genome.name=='HsapE37',genome.strain=='HsapE37');(name=='ABCD1',synonyms=='ABCD1')
 */
const SearchPage: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState<RejectReason>();
  const location = useLocation();
  const searchEntityType = useURLSearchParam("searchType");
  const searchQuery = useURLSearchParamSingleValue("query"); // special hook for getting the full value not split by "," since the query might contain such characters

  const valid = (searchTypeValue: string): boolean => {
    return (
      searchTypeValue === ENTITY_TYPE.GENE ||
      searchTypeValue === ENTITY_TYPE.TRANSCRIPT ||
      searchTypeValue === ENTITY_TYPE.PROTEIN ||
      searchTypeValue === ENTITY_TYPE.SAMPLE ||
      searchTypeValue === ENTITY_TYPE.ORGANISM
    );
  };

  useEffect(() => {
    if (searchQuery != undefined && searchEntityType != undefined && !rejectReason) {
      if (!valid(searchEntityType.toLowerCase())) {
        // basic client side validation
        setRejectReason({
          error: {
            message: `Invalid search type '${searchEntityType}' detected.`,
            graphQLErrors: null,
          },
          payload: null,
          type: "Validation Error",
        });
      } else {
        // run advanced search, the promise will reject if something goes wrong and we set the error object in state
        dispatch(createSearchResultFromURL(searchEntityType, searchQuery))
          .then((searchResult) => {
            // the error handler returns undefined when there was an error (see error-utils.ts:84)
            if (searchResult != null) {
              if (searchResult.groups && searchResult.groups.length === 1 && searchResult.groups[0]?.itemCount === 1) {
                // Redirect to summary page e.g. http://localhost:8080/searchQuery?searchType=gene&query=name=='tcap';genome.name=='mmus33'
                const group: Group = searchResult.groups[0];
                setRejectReason(undefined);
                navigate(`/${group.type}/${group.firstItemId}`);
              } else {
                // Redirect to result list page e.g. http://localhost:8080/searchQuery?searchType=gene&query=name=='tcap'
                setRejectReason(undefined);
                navigate(`/search/${searchResult.id}?externalSearch=true`);
              }
            } else {
              // at this point, there was an error but the promised resolved, the ErrorPage may handle the error
              return;
            }
          })
          .catch((reason) => {
            setRejectReason(reason);
          });
      }
    }
  }, [dispatch, rejectReason, searchEntityType, searchQuery]);

  const processErrorMessage = (msg: string): string => {
    const r = new RegExp("^\\s*No feature type exists for name '(.+?)'");
    const match = r.exec(msg);
    if (match) {
      return "Invalid search type '" + match[1] + "' detected.";
    }
    return msg;
  };

  if (searchQuery == undefined || searchEntityType == undefined) {
    const msg =
      searchQuery == undefined && searchEntityType == undefined
        ? "There is no search type and query defined in the URL."
        : searchEntityType == undefined
          ? "There is no search type defined in the URL."
          : searchQuery == undefined
            ? "There is no search query defined in the URL."
            : undefined;
    return (
      <InformationPage errorTitle={null} noGoBackLink={true}>
        <div>
          Please provide some search criteria in the URL. You will then be automatically redirected to the search
          results.
          <div style={{ marginTop: 21, marginBottom: 13, fontWeight: "bold" }}>{msg}</div>
          <div style={{ marginTop: 21, marginBottom: 13 }}>
            Example:{" "}
            <Link key="the-link-key" to="/searchQuery?searchType=gene&query=name=='abc'">
              /searchQuery?searchType=gene&query=name=='abc'
            </Link>
          </div>
        </div>
      </InformationPage>
    );
  }

  if (rejectReason) {
    const error = rejectReason.error;
    const errorType = error.graphQLErrors && error.graphQLErrors.length ? error.graphQLErrors[0].type : "Error";
    const errorTitle = errorType === VALIDATION_ERROR ? "Validation Error" : errorType;

    //modify error message from MT, ir refers to FeatureTypes but we call it searchType in the UI;
    const errorMessage = processErrorMessage(rejectReason.error.message);

    return (
      <InformationPage
        {...{
          errorCode: 500,
          errorTitle,
          children: (
            <div>
              <div style={{ margin: 21 }}>
                An error occurred for the search query{" "}
                <span style={{ fontFamily: "monospace" }}>{location.search}</span>
              </div>
              <div style={{ maxHeight: 300, overflow: "auto", margin: 21 }}>
                <div>{errorMessage}</div>
              </div>
            </div>
          ),
          noGoBackLink: true,
          showReloadLink: false,
        }}
      />
    );
  }

  return (
    <InformationPage errorTitle={null} noGoBackLink={true}>
      <div>
        Searching...
        <br />
        <LoadingSpinner inCenter />
      </div>
    </InformationPage>
  );
};

export { SearchPage };
